import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiInfo,
  FiRefreshCw,
  FiSearch,
  FiXCircle,
} from "react-icons/fi";
import PageHeader from "../../components/common/PageHeader";
import { fetchSystemLogs } from "../../service/apiService";

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function statusBadgeVariant(status) {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return "success";
    case "interrupted":
      return "warning";
    case "failed":
      return "danger";
    case "started":
      return "info";
    default:
      return "secondary";
  }
}

function statusIcon(status) {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return <FiCheckCircle />;
    case "interrupted":
      return <FiAlertTriangle />;
    case "failed":
      return <FiXCircle />;
    default:
      return <FiClock />;
  }
}

function formatSummary(summary) {
  if (!summary || typeof summary !== "object") {
    return {};
  }

  return summary;
}

function parseSummaryFromDescription(description) {
  const text = String(description || "");
  const match = text.match(/Summary:\s*(\{[\s\S]*\})\s*$/);

  if (!match || !match[1]) {
    return {};
  }

  try {
    const parsed = JSON.parse(match[1]);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

function summaryTone(summary) {
  const status = String(summary?.status || "").toLowerCase();

  if (status === "completed") return "success";
  if (status === "completed_with_errors") return "warning";
  if (status === "interrupted") return "warning";
  if (status === "failed") return "danger";
  return "info";
}

function MetricBox({ label, value, variant = "secondary" }) {
  return (
    <Card className={`border-0 bg-${variant} bg-opacity-10 h-100`}>
      <Card.Body className="py-3">
        <div className="text-muted small text-uppercase">{label}</div>
        <div className={`fs-4 fw-bold text-${variant}`}>{value ?? 0}</div>
      </Card.Body>
    </Card>
  );
}

export default function LogsPage() {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(100);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [activeLogId, setActiveLogId] = useState("");

  useEffect(() => {
    const initialSearch = searchParams.get("search") || "";
    const initialStatus = searchParams.get("status") || "";

    if (initialSearch) {
      setSearch(initialSearch);
    }

    if (initialStatus) {
      setStatus(initialStatus);
    }
  }, [searchParams]);

  const loadLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchSystemLogs({
        search: search.trim(),
        status,
        limit,
      });

      if (res?.status === "success") {
        setLogs(res.data || []);
        setLastLoadedAt(new Date());
      } else {
        setError(res?.message || "Failed to load logs");
      }
    } catch (loadError) {
      console.error("Logs load error:", loadError);
      setError("Server not reachable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, limit]);

  useEffect(() => {
    if (logs.length === 0) {
      setActiveLogId("");
      return;
    }

    if (!search.trim()) {
      return;
    }

    setActiveLogId(String(logs[0]?.id || ""));
  }, [logs, search]);

  const counts = useMemo(() => {
    return logs.reduce(
      (acc, log) => {
        const currentStatus = String(log.status || "").toLowerCase();
        if (currentStatus === "started") acc.started += 1;
        if (currentStatus === "completed") acc.completed += 1;
        if (currentStatus === "interrupted") acc.interrupted += 1;
        if (currentStatus === "failed") acc.failed += 1;
        return acc;
      },
      { started: 0, completed: 0, interrupted: 0, failed: 0 }
    );
  }, [logs]);

  return (
    <Container fluid className="admin-page">
      <PageHeader
        title="Activity Logs"
        subtitle="Track imports and campaigns with expandable summaries"
      />

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Started</div>
              <div className="fs-3 fw-bold">{counts.started}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Completed</div>
              <div className="fs-3 fw-bold text-success">{counts.completed}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Interrupted</div>
              <div className="fs-3 fw-bold text-warning">{counts.interrupted}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Failed</div>
              <div className="fs-3 fw-bold text-danger">{counts.failed}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label>Search logs</Form.Label>
              <div className="position-relative">
                <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  className="ps-5"
                  placeholder="Search action or description"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </Col>

            <Col md={3}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">All statuses</option>
                <option value="started">Started</option>
                <option value="completed">Completed</option>
                <option value="interrupted">Interrupted</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Col>

            <Col md={2}>
              <Form.Label>Limit</Form.Label>
              <Form.Select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </Form.Select>
            </Col>

            <Col md={2} className="d-flex justify-content-md-end">
              <Button variant="outline-primary" onClick={loadLogs} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : <FiRefreshCw className="me-2" />}
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div className="fw-semibold">Logs ({logs.length})</div>
          <div className="text-muted small">
            {lastLoadedAt ? `Last loaded: ${formatDateTime(lastLoadedAt)}` : "Loading on open"}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {loading && logs.length === 0 ? (
            <div className="py-5 text-center text-muted">
              <Spinner animation="border" className="mb-2" />
              <div>Loading logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="py-5 text-center text-muted">No logs found</div>
          ) : (
            <Accordion flush activeKey={activeLogId} onSelect={(eventKey) => setActiveLogId(String(eventKey || ""))}>
              {logs.map((log, index) => {
                const summary = formatSummary(log.summary);
                const summaryFromDescription = parseSummaryFromDescription(log.description);
                const mergedSummary = { ...summaryFromDescription, ...summary };
                const logStatus = String(log.status || "").toLowerCase();
                const summaryStatus = String(mergedSummary.status || logStatus || "").toLowerCase();
                const summaryVariant = summaryTone(mergedSummary);
                const summaryMessage =
                  mergedSummary.message ||
                  (summaryStatus === "completed"
                    ? "Job completed successfully"
                    : summaryStatus === "completed_with_errors"
                      ? "Job completed with some errors"
                      : summaryStatus === "interrupted"
                        ? "Job was interrupted"
                        : summaryStatus === "failed"
                          ? "Job failed"
                          : "Job started");
                const createdAt = formatDateTime(log.created_at);
                const startedAt = formatDateTime(log.started_at);
                const completedAt = formatDateTime(log.completed_at);
                const updatedAt = formatDateTime(log.updated_at);
                const actionText = String(log.action || "").toUpperCase();
                const isCampaign = actionText.includes("WHATSAPP");
                const isImport = actionText.includes("IMPORT");
                const total = Number(mergedSummary.total ?? mergedSummary.selected_contacts ?? 0);
                const successValue = Number(
                  mergedSummary.sent ??
                    mergedSummary.success ??
                    mergedSummary.inserted ??
                    mergedSummary.imported ??
                    0
                );
                const failed = mergedSummary.failed ?? 0;
                const skipped = mergedSummary.skipped ?? 0;
                const invalid = mergedSummary.invalid ?? 0;
                const primaryLabel = isCampaign ? "Sent" : isImport ? "Imported" : "Success";
                const derivedPrimary = Math.max(total - failed - skipped - invalid, 0);
                const primaryCount = successValue > 0 ? successValue : derivedPrimary;

                return (
                  <Accordion.Item eventKey={String(log.id)} key={log.id}>
                    <Accordion.Header>
                      <div className="w-100 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 pe-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="fs-6">{statusIcon(logStatus)}</span>
                          <span className="fw-semibold">{log.action || `Log #${log.id}`}</span>
                          <Badge bg={statusBadgeVariant(logStatus)}>{logStatus || "unknown"}</Badge>
                          <Badge bg="light" text="dark">
                            #{index + 1}
                          </Badge>
                        </div>
                        <div className="text-muted small">
                          {createdAt}
                        </div>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body>
                      <div className="mb-3">
                        <Card className={`border-0 bg-${summaryVariant} bg-opacity-10`}>
                          <Card.Body className="py-3">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <FiInfo className={`text-${summaryVariant}`} />
                              <div className="fw-semibold">{summaryMessage}</div>
                            </div>
                            <div className="text-muted small">
                              {log.action} created on {createdAt}
                              {startedAt !== "-" ? `, started at ${startedAt}` : ""}
                              {completedAt !== "-" ? `, completed at ${completedAt}` : ""}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>

                      <Row className="g-3 mb-3">
                        <Col md={3}>
                          <MetricBox label="Total" value={total} variant="secondary" />
                        </Col>
                        <Col md={3}>
                          <MetricBox label={primaryLabel} value={primaryCount} variant="success" />
                        </Col>
                        <Col md={3}>
                          <MetricBox label="Failed" value={failed} variant="danger" />
                        </Col>
                        <Col md={3}>
                          <MetricBox label="Skipped / Invalid" value={skipped + invalid} variant="warning" />
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col md={6}>
                          <Card className="border-0 bg-light h-100">
                            <Card.Body>
                              <div className="text-muted small mb-2">Timeline</div>
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Created</span>
                                  <strong>{createdAt}</strong>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Started</span>
                                  <strong>{startedAt}</strong>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Completed</span>
                                  <strong>{completedAt}</strong>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Updated</span>
                                  <strong>{updatedAt}</strong>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={6}>
                          <Card className="border-0 bg-light h-100">
                            <Card.Body>
                              <div className="text-muted small mb-2">Context</div>
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Action</span>
                                  <strong>{log.action || "-"}</strong>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Status</span>
                                  <Badge bg={statusBadgeVariant(summaryStatus || logStatus)}>
                                    {summaryStatus || logStatus || "unknown"}
                                  </Badge>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Primary Count</span>
                                  <strong>{primaryCount}</strong>
                                </div>
                                <div className="d-flex justify-content-between gap-2">
                                  <span>Log ID</span>
                                  <strong>#{log.id}</strong>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={12}>
                          <Card className="border-0 bg-white h-100">
                            <Card.Body>
                              <div className="text-muted small mb-2">Counts Report</div>
                              <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                  <tbody>
                                    <tr>
                                      <td className="text-muted">Total processed</td>
                                      <td className="fw-semibold">{total}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">{primaryLabel}</td>
                                      <td className="fw-semibold text-success">{primaryCount}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Failed</td>
                                      <td className="fw-semibold text-danger">{failed}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Skipped</td>
                                      <td className="fw-semibold text-info">{skipped}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Invalid</td>
                                      <td className="fw-semibold text-warning">{invalid}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
